import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

import useTrls from 'hooks/translation/useTrls';

import { BiddingStrategy } from 'store/models/auctionData';
import { setMultiCreationBidValidationError, setMultiCreationBudgetValidationError } from 'store/models/multiCreation';
import { useSelector } from 'store/utils';

const TrlKeys = {
    error: {
        minValue: 'MultiCreationAuction.BidsAndBudgetsTable.error.minValue',
        autoStrategyMinValue: 'MultiCreationAuction.BidsAndBudgetsTable.error.autoStrategyMinValue',
        valueRequired: 'MultiCreationAuction.BidsAndBudgetsTable.error.valueRequired',
        biggerThanBudget: 'MultiCreationAuction.BidsAndBudgetsTable.error.bidBiggerThanBudget',
    },
};

interface UseMultiCreationBidBudgetValidation {
    (args: { vacancyId?: number; biddingStrategy?: BiddingStrategy }): {
        validateBid: (value: string, minValue: number) => string | null;
        validateBudget: (value: string, minValue: number) => string | null;
    };
}

export const useMultiCreationBidBudgetValidation: UseMultiCreationBidBudgetValidation = ({
    vacancyId,
    biddingStrategy,
}) => {
    const auction = useSelector(({ multiCreation }) =>
        vacancyId ? multiCreation.auctionsToCreate[vacancyId] : undefined
    );

    const trls = useTrls();
    const dispatch = useDispatch();

    const validateBid = useCallback(
        (value: string, minValue: number) => {
            let error = null;
            if (value === '') {
                error = trls(TrlKeys.error.valueRequired);
            } else {
                const numericValue = Number(value);

                if (biddingStrategy === BiddingStrategy.Manual && numericValue < minValue) {
                    error = trls(TrlKeys.error.minValue, [minValue / 100]);
                }

                if (biddingStrategy === BiddingStrategy.Auto && numericValue < minValue) {
                    error = trls(TrlKeys.error.autoStrategyMinValue, [minValue / 100]);
                }

                if (vacancyId && auction?.budget != null && numericValue > Number(auction.budget)) {
                    error = trls(TrlKeys.error.biggerThanBudget);
                }
            }

            if (vacancyId && auction?.bidValidationError !== error) {
                dispatch(
                    setMultiCreationBidValidationError({
                        vacancyId,
                        error,
                    })
                );
            }

            return error;
        },
        [auction, biddingStrategy, dispatch, trls, vacancyId]
    );

    const validateBudget = useCallback(
        (value: string, minValue: number) => {
            let error = null;

            if (value === '') {
                error = trls(TrlKeys.error.valueRequired);
            } else {
                const numericValue = Number(value);

                if (numericValue < minValue) {
                    error = trls(TrlKeys.error.minValue, [minValue / 100]);
                }
            }

            if (vacancyId && auction?.budgetValidationError !== error) {
                dispatch(
                    setMultiCreationBudgetValidationError({
                        vacancyId,
                        error,
                    })
                );
            }

            return error;
        },
        [auction, dispatch, trls, vacancyId]
    );

    return { validateBid, validateBudget };
};
